/**
 * Statement PDF Generator
 * Generates downloadable PDF statements for facility billing
 */

/**
 * Format currency value
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

/**
 * Format month string for display
 * @param {string} monthStr - Month in YYYY-MM format
 * @returns {string} Formatted month string
 */
const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/**
 * Format date for display
 * @param {Date|object} date - Date object or Firestore timestamp
 * @returns {string} Formatted date string
 */
const formatDate = (date) => {
  if (!date) return new Date().toLocaleDateString();
  if (date.seconds) {
    return new Date(date.seconds * 1000).toLocaleDateString();
  }
  return new Date(date).toLocaleDateString();
};

/**
 * Generate HTML for the statement
 * @param {object} statement - Statement data
 * @param {object} facility - Facility data
 * @returns {string} HTML string
 */
const generateStatementHTML = (statement, facility) => {
  const lineItemsHtml = (statement.lineItems || []).map(item => `
    <tr>
      <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.description}</td>
      <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
      <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(item.unitPrice)}</td>
      <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(item.amount)}</td>
    </tr>
  `).join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>Monthly Statement - ${formatMonth(statement.month)}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #333;
            line-height: 1.6;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #667eea;
          }
          .logo-section {
            display: flex;
            align-items: center;
            gap: 15px;
          }
          .logo {
            width: 60px;
            height: 60px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 24px;
            font-weight: bold;
          }
          .company-name {
            font-size: 24px;
            font-weight: 700;
            color: #333;
          }
          .company-tagline {
            font-size: 12px;
            color: #666;
          }
          .statement-title {
            text-align: right;
          }
          .statement-title h1 {
            font-size: 28px;
            color: #667eea;
            margin-bottom: 5px;
          }
          .statement-title p {
            color: #666;
            font-size: 14px;
          }
          .info-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
          }
          .info-block h3 {
            font-size: 12px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
          }
          .info-block p {
            font-size: 14px;
            color: #333;
            margin: 2px 0;
          }
          .info-block .highlight {
            font-size: 18px;
            font-weight: 600;
            color: #667eea;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          thead {
            background: #667eea;
            color: white;
          }
          thead th {
            padding: 12px 10px;
            text-align: left;
            font-weight: 600;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          thead th:nth-child(2),
          thead th:nth-child(3),
          thead th:nth-child(4) {
            text-align: right;
          }
          thead th:nth-child(2) {
            text-align: center;
          }
          tbody tr:hover {
            background: #f8f9fa;
          }
          .totals-section {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 40px;
          }
          .totals-box {
            width: 300px;
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
          }
          .totals-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #eee;
          }
          .totals-row:last-child {
            border-bottom: none;
            padding-top: 12px;
            margin-top: 8px;
            border-top: 2px solid #667eea;
          }
          .totals-row.discount {
            color: #28a745;
          }
          .totals-row .label {
            color: #666;
          }
          .totals-row .value {
            font-weight: 500;
          }
          .totals-row:last-child .label,
          .totals-row:last-child .value {
            font-size: 18px;
            font-weight: 700;
            color: #333;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #eee;
            text-align: center;
            color: #666;
            font-size: 12px;
          }
          .footer p {
            margin: 5px 0;
          }
          .no-items {
            text-align: center;
            padding: 40px;
            color: #666;
            font-style: italic;
          }
          @media print {
            body {
              padding: 20px;
            }
            .header {
              border-bottom-color: #667eea !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            thead {
              background: #667eea !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            thead th {
              color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-section">
            <div class="logo">QL</div>
            <div>
              <div class="company-name">QuantumLeap EMR</div>
              <div class="company-tagline">Healthcare Management System</div>
            </div>
          </div>
          <div class="statement-title">
            <h1>STATEMENT</h1>
            <p>Monthly Billing Summary</p>
          </div>
        </div>

        <div class="info-section">
          <div class="info-block">
            <h3>Facility</h3>
            <p class="highlight">${facility.name || 'N/A'}</p>
            <p>ID: ${facility.id || 'N/A'}</p>
          </div>
          <div class="info-block">
            <h3>Billing Period</h3>
            <p class="highlight">${formatMonth(statement.month)}</p>
          </div>
          <div class="info-block">
            <h3>Generated</h3>
            <p>${formatDate(statement.generatedAt)}</p>
            <p>Status: ${statement.status === 'finalized' ? 'Finalized' : 'Draft'}</p>
          </div>
          <div class="info-block">
            <h3>Providers</h3>
            <p class="highlight">${statement.providerCount || 0}</p>
            <p>Active Users</p>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemsHtml || '<tr><td colspan="4" class="no-items">No line items</td></tr>'}
          </tbody>
        </table>

        <div class="totals-section">
          <div class="totals-box">
            <div class="totals-row">
              <span class="label">Subtotal</span>
              <span class="value">${formatCurrency(statement.subtotal)}</span>
            </div>
            ${statement.discountAmount > 0 ? `
              <div class="totals-row discount">
                <span class="label">Discount</span>
                <span class="value">-${formatCurrency(statement.discountAmount)}</span>
              </div>
            ` : ''}
            <div class="totals-row">
              <span class="label">Tax</span>
              <span class="value">${formatCurrency(statement.taxAmount)}</span>
            </div>
            <div class="totals-row">
              <span class="label">Total Due</span>
              <span class="value">${formatCurrency(statement.totalAmount)}</span>
            </div>
          </div>
        </div>

        <div class="footer">
          <p>Thank you for choosing QuantumLeap EMR for your healthcare management needs.</p>
          <p>For billing inquiries, please contact support@quantumleap-emr.com</p>
          <p>Statement generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
      </body>
    </html>
  `;
};

/**
 * Generate and download a PDF statement
 * Uses the browser's print functionality to generate a PDF
 * @param {object} statement - Statement data with lineItems, totals, etc.
 * @param {object} facility - Facility data with name, id
 */
export const generateStatementPdf = async (statement, facility) => {
  // Generate HTML content
  const html = generateStatementHTML(statement, facility);
  
  // Open in new window for printing/saving as PDF
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (!printWindow) {
    throw new Error('Unable to open print window. Please check your popup blocker settings.');
  }

  printWindow.document.write(html);
  printWindow.document.close();
  
  // Wait for content to load, then trigger print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return true;
};

/**
 * Generate statement HTML as a blob for direct download
 * Alternative method that doesn't require popup
 * @param {object} statement - Statement data
 * @param {object} facility - Facility data
 * @returns {Blob} HTML blob
 */
export const generateStatementBlob = (statement, facility) => {
  const html = generateStatementHTML(statement, facility);
  return new Blob([html], { type: 'text/html' });
};

/**
 * Download statement as HTML file
 * Fallback option if print-to-PDF doesn't work
 * @param {object} statement - Statement data
 * @param {object} facility - Facility data
 */
export const downloadStatementHtml = (statement, facility) => {
  const blob = generateStatementBlob(statement, facility);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `statement-${facility.name?.replace(/\s+/g, '-') || 'facility'}-${statement.month}.html`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

const statementPdfUtils = {
  generateStatementPdf,
  generateStatementBlob,
  downloadStatementHtml
};

export default statementPdfUtils;
